import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { useAuth, type Reconnect } from '../modules/identity/index.ts';

/** Seconds until the next try, ticking once a second. */
function useSecondsLeft(reconnect: Reconnect) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!reconnect) return;
    // From now, not from when the strip was first drawn: the count starts at the backoff's own wait.
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [reconnect]);
  return reconnect ? Math.max(0, Math.ceil((reconnect.retryAt - now) / 1000)) : 0;
}

/**
 * "Reconnecting…" while the platform does not answer (the api restarts, Board #2649): the person stays on
 * their page and stays signed in; the app tries again with a backoff, or now on "Retry now". A strip over
 * the top of every page (the routes' guards render it), so no page's own layout moves.
 */
export function ReconnectingBanner() {
  const { reconnect, retryNow } = useAuth();
  const left = useSecondsLeft(reconnect);
  if (!reconnect) return null;
  return (
    // An opaque strip (the page's background under the amber tint): nothing of the page shows through.
    <div className="fixed top-0 inset-x-0 z-[1000] bg-page-bg shadow-md">
      <div
        role="status"
        aria-live="polite"
        data-testid="reconnecting"
        className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2 text-sm border-b border-[var(--pill-amber-bg)] bg-[var(--pill-amber-bg)] text-[var(--pill-amber-dark)]"
      >
        <Loader2 size={14} className="shrink-0 animate-spin" />
        <span className="font-semibold">Reconnecting…</span>
        <span>
          The platform is not answering (it may be restarting).{' '}
          {left > 0 ? `Trying again in ${left} s.` : 'Trying again now.'}
        </span>
        <button
          type="button"
          onClick={retryNow}
          data-testid="reconnecting-retry"
          className="ml-auto font-semibold underline bg-transparent border-0 p-0 text-inherit cursor-pointer"
        >
          Retry now
        </button>
      </div>
    </div>
  );
}

export function ReconnectingScreen() {
  return (
    <div className="min-h-screen bg-page-bg flex flex-col">
      <ReconnectingBanner />
    </div>
  );
}
